/**
 * Session — état d'authentification réel (Worker /auth/*).
 * Le token (Bearer) est persisté côté navigateur ; le cookie HttpOnly reste
 * la voie privilégiée en navigateur. Aucun profil fictif : sans session,
 * `user` est null et les gardes redirigent vers /connexion.
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { SessionUser } from '../../../lib/auth/permissions';

const TOKEN_KEY = 'nexacraft:token';

export function loadStoredToken(): string | null {
  try {
    return typeof window !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null;
  } catch {
    return null;
  }
}

function storeToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // stockage indisponible (navigation privée) : session mémoire uniquement
  }
}

interface SessionState {
  /** Utilisateur authentifié, ou null si visiteur. */
  user: SessionUser | null;
  /** Token Bearer courant (null en cookie pur). */
  token: string | null;
  /** Vrai tant que la session n'a pas été vérifiée auprès du Worker. */
  sessionChecked: boolean;
}

const initialState: SessionState = {
  user: null,
  token: loadStoredToken(),
  sessionChecked: false,
};

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    credentialsReceived(state, action: PayloadAction<{ token?: string | null; user: SessionUser }>) {
      state.user = action.payload.user;
      if (action.payload.token) {
        state.token = action.payload.token;
        storeToken(action.payload.token);
      }
      state.sessionChecked = true;
    },
    sessionResolved(state, action: PayloadAction<SessionUser | null>) {
      state.user = action.payload;
      state.sessionChecked = true;
      if (!action.payload) {
        state.token = null;
        storeToken(null);
      }
    },
    loggedOut(state) {
      state.user = null;
      state.token = null;
      storeToken(null);
      state.sessionChecked = true;
    },
  },
});

export const { credentialsReceived, sessionResolved, loggedOut } = sessionSlice.actions;
